'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { PlusCircle, Calendar, Trash2, X, AlertTriangle } from 'lucide-react';
import { useFasesServicio } from '@/components/hooks/useFasesServicio';
import { useRef, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

type Props = {
  serviceId: string;
  open: boolean;
  onClose: () => void;
};

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
`;

// ============================================================
// Helpers duración
// ============================================================
function parseDuracion(val: string | number) {
  const total = parseInt(String(val || '0'), 10);
  const dias = Math.floor(total / 1440);
  const horas = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  return { total, dias, horas, mins };
}

function buildDuracion(dias: number, horas: number, mins: number) {
  return String(dias * 1440 + horas * 60 + mins);
}

function formatDuracionResumen(total: number): string {
  if (total <= 0) return 'Sin duración';
  const dias = Math.floor(total / 1440);
  const horas = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  const partes = [
    dias > 0 && `${dias}d`,
    horas > 0 && `${horas}h`,
    mins > 0 && `${mins}min`,
  ].filter(Boolean);
  return partes.join(' ');
}

// ============================================================
// DurationPicker
// ============================================================
function DurationPicker({
  value,
  onChange,
}: {
  value: string | number;
  onChange: (val: number) => void;
}) {
  const [showDays, setShowDays] = useState(false);
  const { total, dias, horas, mins } = parseDuracion(value);

  const emit = (d: number, h: number, m: number) =>
    onChange(parseInt(buildDuracion(d, h, m), 10));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setShowDays((v) => !v)}
          title={showDays ? 'Ocultar días' : 'Añadir días'}
          className={`shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${
            dias > 0 || showDays
              ? 'border-[#103254] bg-[#103254]/10 text-[#103254] dark:border-blue-400 dark:bg-blue-400/10 dark:text-blue-400'
              : 'border-gray-300 dark:border-white/10 text-gray-400 hover:border-gray-400 dark:hover:border-white/20'
          }`}
        >
          <Calendar className="w-4 h-4" />
        </button>

        <div className="flex-1">
          <Select
            value={String(horas)}
            onValueChange={(v) => emit(dias, parseInt(v, 10), mins)}
          >
            <SelectTrigger className="h-9 text-sm w-full focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {Array.from({ length: 24 }, (_, i) => (
                <SelectItem key={i} value={String(i)} className="text-sm">
                  {i}h
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <span className="text-gray-400 text-sm shrink-0">:</span>

        <div className="flex-1">
          <Select
            value={String(mins)}
            onValueChange={(v) => emit(dias, horas, parseInt(v, 10))}
          >
            <SelectTrigger className="h-9 text-sm w-full focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                <SelectItem key={m} value={String(m)} className="text-sm">
                  {m}min
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showDays && (
        <div className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg">
          <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
            Días:
          </span>
          <Select
            value={String(dias)}
            onValueChange={(v) => emit(parseInt(v, 10), horas, mins)}
          >
            <SelectTrigger className="h-9 text-sm flex-1 focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {Array.from({ length: 31 }, (_, i) => (
                <SelectItem key={i} value={String(i)} className="text-sm">
                  {i} {i === 1 ? 'día' : 'días'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {total > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
          {formatDuracionResumen(total)}
          <span className="ml-1 text-gray-300 dark:text-gray-600">
            ({total} min)
          </span>
        </p>
      )}
    </div>
  );
}

// ============================================================
// Componente principal
// ============================================================
export const EditPhasesServiceDialog = ({
  serviceId,
  open,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { phases, loading, addPhase, removePhase, updateField, save } =
    useFasesServicio(serviceId, open);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Duracion del propio servicio, para avisar si las fases no cuadran con ella.
  const [duracionServicio, setDuracionServicio] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !serviceId) return;
    let cancelado = false;

    supabase
      .from('servicios')
      .select('duracion_minutos')
      .eq('id_servicio', serviceId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelado) setDuracionServicio(data?.duracion_minutos ?? null);
      });

    return () => {
      cancelado = true;
    };
  }, [open, serviceId]);

  const totalFases = phases.reduce(
    (acc, f) => acc + (parseInt(String(f.duracion_minutos), 10) || 0),
    0
  );
  const desvio =
    duracionServicio != null ? totalFases - duracionServicio : 0;

  const handleAddPhase = () => {
    addPhase();
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 50);
  };

  const handleSave = async () => {
    const ok = await save();
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={SHEET_CLASS}>
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {t('editPhasesDialog.title')}
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {phases.length > 0 && duracionServicio != null && (
          <div className="px-4 sm:px-6 pb-3 shrink-0">
            <div
              className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${
                desvio === 0
                  ? 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-300'
                  : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
              }`}
            >
              {desvio !== 0 && (
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <div>
                <div>
                  {formatDuracionResumen(totalFases)} en fases ·{' '}
                  {formatDuracionResumen(duracionServicio)} de servicio
                </div>
                {desvio > 0 && (
                  <div className="mt-0.5">
                    Te pasas {desvio} min. La cita se pintará más larga que la
                    duración del servicio.
                  </div>
                )}
                {desvio < 0 && (
                  <div className="mt-0.5">
                    Faltan {Math.abs(desvio)} min por repartir en fases.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5"
        >
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4">
            {loading ? (
              <div className="h-40 flex justify-center items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#103254] dark:border-blue-400" />
              </div>
            ) : phases.length === 0 ? (
              <div className="flex flex-col items-center p-6 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl">
                <PlusCircle className="w-12 h-12 text-[#103254] dark:text-blue-400" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                  {t('editPhasesDialog.noPhases')}
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {t('editPhasesDialog.noPhasesText')}
                </p>
                <Button
                  onClick={handleAddPhase}
                  variant="outline"
                  className="w-full rounded-lg font-medium text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 hover:bg-gray-100 dark:hover:bg-white/10 mt-4 transition-colors"
                >
                  {t('editPhasesDialog.addFirst')}
                </Button>
              </div>
            ) : (
              phases.map((phase, i) => (
                <div
                  key={phase.id_fase || i}
                  className="border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-white/5"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                      Fase {i + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePhase(i)}
                      className="h-7 w-7 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-400/10 transition-colors"
                      title="Eliminar fase"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('editPhasesDialog.labelName')}
                    </label>
                    <Input
                      value={phase.nombre_fase}
                      onChange={(e) =>
                        updateField(i, 'nombre_fase', e.target.value)
                      }
                      className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('editPhasesDialog.labelDuration')}
                    </label>
                    <DurationPicker
                      value={phase.duracion_minutos}
                      onChange={(val) => updateField(i, 'duracion_minutos', val)}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('editPhasesDialog.labelAttention')}
                    </label>
                    <div className="flex items-center gap-3 mt-1">
                      <Switch
                        checked={phase.requiere_atencion}
                        onCheckedChange={(checked) =>
                          updateField(i, 'requiere_atencion', checked)
                        }
                      />
                      <span
                        className={
                          phase.requiere_atencion
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-500 dark:text-red-400'
                        }
                      >
                        {phase.requiere_atencion
                          ? t('editPhasesDialog.attentionYes')
                          : t('editPhasesDialog.attentionNo')}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {phases.length > 0 && (
          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={handleAddPhase}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              {t('editPhasesDialog.addPhase')}
            </Button>

            <Button
              onClick={handleSave}
              disabled={loading}
              className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
            >
              {t('editPhasesDialog.save')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};